import { useState, useEffect } from "react";
import api from "../lib/api";
import {
  Zap, Plus, Bell, Mail, MessageSquare, Clock, Trash2, Play,
  GitBranch, Filter, ArrowRight, ChevronDown, ChevronUp, Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const triggerLabels = {
  appointment_reminder_24h: "24hr Reminder", post_visit: "After Visit", no_show: "No-Show",
  birthday: "Birthday", inactive_30d: "Inactive 30d", appointment_booked: "Booking Confirmed"
};
const stepIcons = { condition: Filter, delay: Clock, action: Mail, branch: GitBranch };
const stepColors = { condition: "#805B10", delay: "#274F7D", action: "#2D5936", branch: "#8B5CF6" };

function StepCard({ step, index, onRemove, onUpdate }) {
  const Icon = stepIcons[step.type] || Zap;
  const color = stepColors[step.type] || "#5E5E5E";
  const cfg = step.config || {};
  return (
    <div className="relative" data-testid={`step-${index}`}>
      {index > 0 && <div className="absolute left-6 -top-4 w-0.5 h-4 bg-[#E6E4E0]" />}
      <div className="flex items-start gap-3 bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{step.type}</span>
            <span className="text-[10px] text-slate-400">Step {index + 1}</span>
          </div>
          {step.type === "delay" && (
            <p className="text-sm text-slate-600">Wait <span className="font-medium text-slate-900">{cfg.hours || 0} hours</span></p>
          )}
          {step.type === "condition" && (
            <p className="text-sm text-slate-600">If <span className="font-medium text-slate-900">{cfg.field}</span> {cfg.operator} <span className="font-medium text-slate-900">{cfg.value}</span></p>
          )}
          {step.type === "action" && (
            <div>
              <p className="text-xs text-slate-400 mb-0.5">{cfg.action_type?.replace('_', ' ')}</p>
              <p className="text-sm text-slate-600 italic">"{cfg.template?.substring(0, 80)}{cfg.template?.length > 80 ? '...' : ''}"</p>
            </div>
          )}
          {step.type === "branch" && (
            <div className="text-sm">
              <p className="text-slate-600">If <span className="font-medium text-slate-900">{cfg.condition}</span></p>
              <div className="flex gap-4 mt-1 text-xs">
                <span className="text-emerald-600">Yes: {cfg.true_action}</span>
                <span className="text-rose-600">No: {cfg.false_action}</span>
              </div>
            </div>
          )}
        </div>
        {onRemove && (
          <button onClick={() => onRemove(index)} className="text-slate-400 hover:text-rose-600 p-1">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function NotificationsPage() {
  const [automations, setAutomations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showBuilder, setShowBuilder] = useState(null); // automation being edited
  const [expandedAuto, setExpandedAuto] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [newAuto, setNewAuto] = useState({ name: "", trigger: "appointment_booked", action: "send_email", template: "", is_active: true, delay_hours: 0, steps: [] });
  const [newStep, setNewStep] = useState({ type: "action", config: {} });

  useEffect(() => {
    const load = async () => {
      try {
        const [autoRes, notifRes] = await Promise.all([api.get("/automations"), api.get("/notifications?limit=30")]);
        setAutomations(autoRes.data); setNotifications(notifRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const createAutomation = async () => {
    try {
      await api.post("/automations", newAuto);
      toast.success("Automation created");
      setShowNew(false);
      setNewAuto({ name: "", trigger: "appointment_booked", action: "send_email", template: "", is_active: true, delay_hours: 0, steps: [] });
      const res = await api.get("/automations"); setAutomations(res.data);
    } catch (err) { toast.error("Failed"); }
  };

  const toggleAutomation = async (id, isActive) => {
    await api.put(`/automations/${id}`, { is_active: !isActive });
    setAutomations(prev => prev.map(a => a.automation_id === id ? { ...a, is_active: !isActive } : a));
  };

  const deleteAutomation = async (id) => {
    await api.delete(`/automations/${id}`);
    setAutomations(prev => prev.filter(a => a.automation_id !== id));
    toast.success("Deleted");
  };

  const testAutomation = async (id) => {
    try {
      const res = await api.post(`/automations/${id}/test`);
      setTestResult(res.data);
      toast.success(`Test complete: ${res.data.steps_executed} steps executed`);
    } catch (err) { toast.error("Test failed"); }
  };

  const saveSteps = async (autoId, steps) => {
    await api.put(`/automations/${autoId}`, { steps });
    setAutomations(prev => prev.map(a => a.automation_id === autoId ? { ...a, steps } : a));
    toast.success("Steps saved");
    setShowBuilder(null);
  };

  const addStepToNew = () => {
    let config = {};
    if (newStep.type === "delay") config = { hours: 24 };
    if (newStep.type === "condition") config = { field: "visit_count", operator: "greater_than", value: "0" };
    if (newStep.type === "action") config = { action_type: "send_email", template: "" };
    if (newStep.type === "branch") config = { condition: "", true_action: "", false_action: "" };
    setNewAuto(p => ({ ...p, steps: [...p.steps, { type: newStep.type, config }] }));
  };

  const removeStepFromNew = (idx) => {
    setNewAuto(p => ({ ...p, steps: p.steps.filter((_, i) => i !== idx) }));
  };

  const totalSent = automations.reduce((s, a) => s + (a.sent_count || 0), 0);
  const activeCount = automations.filter(a => a.is_active).length;
  const multiStepCount = automations.filter(a => (a.steps || []).length > 0).length;

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Automations</h1>
          <p className="text-slate-600 text-sm mt-1">Multi-step workflows with conditional logic</p>
        </div>
        <Button data-testid="new-automation-btn" onClick={() => setShowNew(true)} className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-10 px-5 text-sm">
          <Plus className="w-4 h-4 mr-1.5" /> Build Automation
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Active", value: activeCount, color: "#2D5936" },
          { label: "Multi-Step", value: multiStepCount, color: "#8B5CF6" },
          { label: "Total Sent", value: totalSent, color: "#1A1A1A" },
          { label: "Recent", value: notifications.length, color: "#5E5E5E" },
        ].map(s => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{s.label}</span>
            <p className="font-['Outfit'] text-2xl font-medium mt-1" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="automations">
        <TabsList className="bg-[#FAFAFA] p-1 rounded-lg border border-slate-200 mb-4">
          <TabsTrigger value="automations" className="text-sm">Workflows</TabsTrigger>
          <TabsTrigger value="history" className="text-sm">History</TabsTrigger>
        </TabsList>

        <TabsContent value="automations">
          <div className="space-y-3">
            {automations.map(auto => {
              const steps = auto.steps || [];
              const isExpanded = expandedAuto === auto.automation_id;
              return (
                <div key={auto.automation_id} className={`bg-white border rounded-2xl transition-all ${auto.is_active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`} data-testid={`auto-${auto.automation_id}`}>
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${auto.is_active ? 'bg-[#E8F0E9]' : 'bg-[#FAFAFA]'}`}>
                          {steps.length > 1 ? <GitBranch className="w-5 h-5 text-[#8B5CF6]" /> : <Mail className="w-5 h-5 text-emerald-600" />}
                        </div>
                        <div>
                          <p className="text-base font-medium text-slate-900">{auto.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#FAFAFA] text-slate-600">{triggerLabels[auto.trigger] || auto.trigger}</span>
                            {steps.length > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-[#8B5CF615] text-[#8B5CF6]">{steps.length} steps</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-400">{auto.sent_count || 0} sent</span>
                        <Button data-testid={`test-${auto.automation_id}`} onClick={() => testAutomation(auto.automation_id)} variant="outline" size="sm" className="h-8 border-slate-200 rounded-lg text-xs">
                          <Play className="w-3 h-3 mr-1" /> Test
                        </Button>
                        <Switch checked={auto.is_active} onCheckedChange={() => toggleAutomation(auto.automation_id, auto.is_active)} />
                        {steps.length > 0 && (
                          <button onClick={() => setExpandedAuto(isExpanded ? null : auto.automation_id)} className="text-slate-400 hover:text-slate-900 p-1">
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        )}
                        <button onClick={() => deleteAutomation(auto.automation_id)} className="text-slate-400 hover:text-rose-600 p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {/* Simple automation template */}
                    {steps.length === 0 && auto.template && (
                      <p className="text-xs text-slate-400 mt-3 bg-[#FAFAFA] rounded-lg p-3 italic">"{auto.template}"</p>
                    )}
                  </div>
                  {/* Expanded multi-step view */}
                  {isExpanded && steps.length > 0 && (
                    <div className="border-t border-slate-200 p-5 bg-[#FAFAFA] rounded-b-xl">
                      <div className="flex items-center gap-2 mb-4">
                        <Zap className="w-4 h-4 text-rose-400" />
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Workflow Steps</span>
                      </div>
                      <div className="space-y-3 ml-2">
                        {/* Trigger */}
                        <div className="flex items-center gap-3 bg-slate-900 text-white rounded-lg px-4 py-2.5">
                          <Zap className="w-4 h-4" />
                          <span className="text-sm font-medium">Trigger: {triggerLabels[auto.trigger] || auto.trigger}</span>
                        </div>
                        <div className="ml-6 border-l-2 border-slate-200 pl-4 space-y-3">
                          {steps.map((step, i) => <StepCard key={`${auto.automation_id}-step-${i}`} step={step} index={i} />)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead><tr className="bg-[#FAFAFA] border-b border-slate-200">
                <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Automation</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Trigger</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
                <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Date</th>
              </tr></thead>
              <tbody>
                {notifications.map(n => (
                  <tr key={n.notification_id} className="border-b border-slate-200 hover:bg-[#FAFAFA]">
                    <td className="px-6 py-3 text-sm text-slate-900">{n.automation_name || n.type || "Manual"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{triggerLabels[n.trigger] || n.trigger || "—"}</td>
                    <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-[#E8F0E9] text-emerald-600">{n.status}</span></td>
                    <td className="px-6 py-3 text-xs text-slate-400">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</td>
                  </tr>
                ))}
                {notifications.length === 0 && <tr><td colSpan={4} className="px-6 py-8 text-center text-sm text-slate-400">No history yet</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Test Result Dialog */}
      <Dialog open={!!testResult} onOpenChange={() => setTestResult(null)}>
        <DialogContent className="sm:max-w-[480px] bg-white border-slate-200 rounded-2xl p-8" data-testid="test-result-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">Test Run: {testResult?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-4">
            {(testResult?.test_log || []).map((log, i) => (
              <div key={`log-${log.type || ""}-${i}`} className="flex items-center gap-3 p-3 bg-[#FAFAFA] rounded-lg">
                <div className="w-6 h-6 rounded-full bg-[#E8F0E9] flex items-center justify-center"><Check className="w-3.5 h-3.5 text-emerald-600" /></div>
                <div>
                  <span className="text-xs font-bold uppercase text-slate-400">{log.type}</span>
                  <p className="text-sm text-slate-900">{log.detail}</p>
                </div>
              </div>
            ))}
            <p className="text-xs text-rose-400 text-center pt-2">{testResult?.steps_executed} steps executed successfully</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Automation Builder */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-[600px] bg-white border-slate-200 rounded-2xl p-8 max-h-[85vh] overflow-y-auto" data-testid="automation-builder-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">Build Automation</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <Input data-testid="auto-name" value={newAuto.name} onChange={e => setNewAuto(p => ({...p, name: e.target.value}))} placeholder="Automation name" className="h-11 border-slate-200 rounded-lg" />
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1.5 block">Trigger</label>
              <Select value={newAuto.trigger} onValueChange={v => setNewAuto(p => ({...p, trigger: v}))}>
                <SelectTrigger data-testid="auto-trigger" className="h-11 border-slate-200 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(triggerLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-slate-600">Workflow Steps</label>
                <span className="text-xs text-slate-400">{newAuto.steps.length} steps</span>
              </div>
              {newAuto.steps.length > 0 && (
                <div className="space-y-2 mb-3">
                  {newAuto.steps.map((step, i) => (
                    <StepCard key={`new-step-${step.type || ""}-${i}`} step={step} index={i} onRemove={removeStepFromNew} />
                  ))}
                </div>
              )}
              {/* Add step */}
              <div className="flex gap-2">
                <Select value={newStep.type} onValueChange={v => setNewStep({ type: v, config: {} })}>
                  <SelectTrigger className="h-9 border-slate-200 rounded-lg text-sm flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="action">Action (Send Email/SMS)</SelectItem>
                    <SelectItem value="delay">Delay (Wait)</SelectItem>
                    <SelectItem value="condition">Condition (If/Then)</SelectItem>
                    <SelectItem value="branch">Branch (If/Else)</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="add-step-btn" onClick={addStepToNew} variant="outline" size="sm" className="h-9 border-slate-200 rounded-lg">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add
                </Button>
              </div>
              {/* Step config for last added step */}
              {newAuto.steps.length > 0 && (
                <div className="mt-3 p-3 bg-[#FAFAFA] rounded-lg space-y-2">
                  <p className="text-xs font-bold text-slate-400 uppercase">Configure Step {newAuto.steps.length}</p>
                  {newAuto.steps[newAuto.steps.length - 1].type === "delay" && (
                    <Input type="number" placeholder="Hours to wait" value={newAuto.steps[newAuto.steps.length - 1].config.hours || ""}
                      onChange={e => { const s = [...newAuto.steps]; s[s.length-1].config.hours = Number(e.target.value); setNewAuto(p => ({...p, steps: s})); }}
                      className="h-9 border-slate-200 rounded-lg text-sm" />
                  )}
                  {newAuto.steps[newAuto.steps.length - 1].type === "action" && (
                    <>
                      <Select value={newAuto.steps[newAuto.steps.length-1].config.action_type || "send_email"}
                        onValueChange={v => { const s = [...newAuto.steps]; s[s.length-1].config.action_type = v; setNewAuto(p => ({...p, steps: s})); }}>
                        <SelectTrigger className="h-9 border-slate-200 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="send_email">Email</SelectItem><SelectItem value="send_sms">SMS</SelectItem></SelectContent>
                      </Select>
                      <Textarea placeholder="Message template..." rows={2} value={newAuto.steps[newAuto.steps.length-1].config.template || ""}
                        onChange={e => { const s = [...newAuto.steps]; s[s.length-1].config.template = e.target.value; setNewAuto(p => ({...p, steps: s})); }}
                        className="border-slate-200 rounded-lg text-sm" />
                    </>
                  )}
                  {newAuto.steps[newAuto.steps.length - 1].type === "condition" && (
                    <div className="grid grid-cols-3 gap-2">
                      <Input placeholder="Field" value={newAuto.steps[newAuto.steps.length-1].config.field || ""}
                        onChange={e => { const s = [...newAuto.steps]; s[s.length-1].config.field = e.target.value; setNewAuto(p => ({...p, steps: s})); }}
                        className="h-9 border-slate-200 rounded-lg text-sm" />
                      <Select value={newAuto.steps[newAuto.steps.length-1].config.operator || "equals"}
                        onValueChange={v => { const s = [...newAuto.steps]; s[s.length-1].config.operator = v; setNewAuto(p => ({...p, steps: s})); }}>
                        <SelectTrigger className="h-9 border-slate-200 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="equals">Equals</SelectItem><SelectItem value="not_equals">Not Equals</SelectItem><SelectItem value="greater_than">Greater Than</SelectItem></SelectContent>
                      </Select>
                      <Input placeholder="Value" value={newAuto.steps[newAuto.steps.length-1].config.value || ""}
                        onChange={e => { const s = [...newAuto.steps]; s[s.length-1].config.value = e.target.value; setNewAuto(p => ({...p, steps: s})); }}
                        className="h-9 border-slate-200 rounded-lg text-sm" />
                    </div>
                  )}
                  {newAuto.steps[newAuto.steps.length - 1].type === "branch" && (
                    <div className="space-y-2">
                      <Input placeholder="Condition (e.g., visit_count > 3)" value={newAuto.steps[newAuto.steps.length-1].config.condition || ""}
                        onChange={e => { const s = [...newAuto.steps]; s[s.length-1].config.condition = e.target.value; setNewAuto(p => ({...p, steps: s})); }}
                        className="h-9 border-slate-200 rounded-lg text-sm" />
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="If true..." value={newAuto.steps[newAuto.steps.length-1].config.true_action || ""}
                          onChange={e => { const s = [...newAuto.steps]; s[s.length-1].config.true_action = e.target.value; setNewAuto(p => ({...p, steps: s})); }}
                          className="h-9 border-slate-200 rounded-lg text-sm" />
                        <Input placeholder="If false..." value={newAuto.steps[newAuto.steps.length-1].config.false_action || ""}
                          onChange={e => { const s = [...newAuto.steps]; s[s.length-1].config.false_action = e.target.value; setNewAuto(p => ({...p, steps: s})); }}
                          className="h-9 border-slate-200 rounded-lg text-sm" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button data-testid="create-auto-btn" onClick={createAutomation} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11">
              Create Automation
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
